import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../utils/cn';
import Section from '../Section';
import { RAID_PEOPLE_INTERVIEWS } from '../content';
import styles from './PeopleInterviews.module.css';

const ASSET_BASE = `${process.env.PUBLIC_URL}/assets/images/raid2026`;
const WANTEDLY_STORIES_URL =
  'https://www.wantedly.com/companies/company_5841931/stories';
const DESKTOP_CAROUSEL_START_OFFSET_FROM_END = 2;
const DEFAULT_CARD_TITLE = '社員インタビュー';
const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
const MARQUEE_BASE_CARD_COUNT = 8;
const MARQUEE_BASE_DURATION_SEC = 58;

export function getDesktopCarouselStartIndex(interviews) {
  return Math.max(
    0,
    interviews.length - DESKTOP_CAROUSEL_START_OFFSET_FROM_END
  );
}

function rotateInterviews(interviews) {
  const startIndex = getDesktopCarouselStartIndex(interviews);

  if (startIndex <= 0) {
    return interviews;
  }

  return [...interviews.slice(startIndex), ...interviews.slice(0, startIndex)];
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query);
    const handleChange = event => {
      setMatches(event.matches);
    };

    setMatches(mediaQueryList.matches);
    mediaQueryList.addEventListener('change', handleChange);

    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}

function InterviewCard({ interview, index, duplicate = false }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const title = interview.title ?? DEFAULT_CARD_TITLE;

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete) {
      setLoaded(true);
    }
  }, []);

  return (
    <li className={styles.item}>
      <a
        className={styles.card}
        href={interview.href}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={duplicate ? -1 : undefined}
      >
        <span className={styles.media}>
          {!loaded && <span className={styles.skeleton} aria-hidden="true" />}
          <img
            ref={imgRef}
            className={cn(styles.image, loaded && styles.imageLoaded)}
            src={`${ASSET_BASE}/${interview.image}`}
            alt={`${interview.label} ${title}`}
            loading={index === 0 && !duplicate ? 'eager' : 'lazy'}
            decoding="async"
            sizes="(max-width: 767px) 78vw, 22rem"
            width="1200"
            height="800"
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
          />
        </span>
        <span className={styles.body}>
          <span className={styles.label}>{interview.label}</span>
          <span className={styles.title}>{title}</span>
          <span className={styles.meta}>
            <span className={styles.department}>{interview.department}</span>
            <span className={styles.metaDivider} aria-hidden="true">
              /
            </span>
            <span className={styles.role}>{interview.role}</span>
          </span>
          <span className={styles.cta}>READ STORY</span>
        </span>
      </a>
    </li>
  );
}

export default function PeopleInterviews() {
  const carouselRef = useRef(null);
  const [marqueePaused, setMarqueePaused] = useState(false);
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_MEDIA_QUERY);
  const showDuplicateGroup = !isMobile;

  const validInterviews = RAID_PEOPLE_INTERVIEWS.filter(({ href }) =>
    Boolean(href)
  );
  const marqueeDurationSec =
    (validInterviews.length / MARQUEE_BASE_CARD_COUNT) *
    MARQUEE_BASE_DURATION_SEC;
  const interviews = isMobile
    ? validInterviews
    : rotateInterviews(validInterviews);

  useEffect(() => {
    if (isMobile || prefersReducedMotion) {
      setMarqueePaused(false);
      return undefined;
    }

    const carousel = carouselRef.current;
    if (!carousel) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setMarqueePaused(!entry.isIntersecting);
      },
      { rootMargin: '100px', threshold: 0 }
    );

    observer.observe(carousel);

    return () => {
      observer.disconnect();
    };
  }, [isMobile, prefersReducedMotion]);

  if (interviews.length === 0) {
    return null;
  }

  return (
    <Section
      id="people"
      eyebrow="PEOPLE / WANTEDLY STORY"
      title="働く人を知る"
      containerClassName="mx-auto w-full max-w-6xl"
      className={styles.section}
    >
      <div className={styles.lead}>
        <p>
          日本トレカセンターで挑戦するメンバーの
          <br className={styles.leadBreak} aria-hidden="true" />
          スト{'\u2060'}ーリーを、Wantedlyで公開しています。
        </p>
      </div>

      <div
        ref={carouselRef}
        className={styles.carousel}
        style={{ '--people-marquee-duration': `${marqueeDurationSec}s` }}
        aria-label="社員インタビュー記事"
      >
        <div className={cn(styles.track, marqueePaused && styles.trackPaused)}>
          <ul className={styles.group}>
            {interviews.map((interview, index) => (
              <InterviewCard
                interview={interview}
                index={index}
                key={interview.id}
              />
            ))}
          </ul>
          {showDuplicateGroup && (
            <ul className={styles.group} aria-hidden="true">
              {interviews.map((interview, index) => (
                <InterviewCard
                  duplicate
                  interview={interview}
                  index={index}
                  key={`${interview.id}-duplicate`}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.storiesLinkWrap}>
        <a
          className={styles.storiesLink}
          href={WANTEDLY_STORIES_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className={styles.storiesLinkLabel}>
            全てのストーリーを見る
          </span>
          <span className={styles.storiesLinkMeta}>WANTEDLY STORIES</span>
        </a>
      </div>
    </Section>
  );
}
